import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";

import { useAuth } from "../shared/auth-context.tsx";
import { inboxApi } from "../shared/api.ts";
import { toggleTheme } from "../shared/theme.ts";
import { useEntitlements } from "../hooks/useEntitlements.ts";
import { GATED_NAV_PATHS, visibleNavPaths } from "../shared/navServices.mjs";

/**
 * Operator command palette (C3) — a centered ⌘K/Ctrl-K modal to jump to any
 * page, run a context action, or open any conversation by typing.
 *
 * Mounted once in AppShell, which owns the open/close state + the global
 * keydown listener. This component owns the search input, the grouped result
 * list, keyboard navigation (↑/↓/Enter/Esc), the focus trap, and focus
 * restoration on close.
 *
 * Design: reuses the app's modal-scrim pattern (theme-aware backdrop) but
 * centers an OPAQUE card high in the viewport (Linear/Raycast placement).
 * Theme toggle + sign-out reuse the app's real mechanisms; conversation
 * results reuse inboxApi.list() and the inbox session deep-link.
 */

type CommandGroup = "Navigate" | "Action" | "Conversation";

interface Command {
  id: string;
  title: string;
  group: CommandGroup;
  hint?: string;
  keywords?: string;
  run: () => void;
}

/** Stable group order + per-group result cap. */
const GROUP_ORDER: CommandGroup[] = ["Navigate", "Action", "Conversation"];
const NAV_CAP = 8;
const ACTION_CAP = 8;
const CONVO_CAP = 6;

/** Routes mirror App.tsx's protected route table (the real paths). */
const NAV_ROUTES: { path: string; label: string; keywords?: string }[] = [
  { path: "/overview", label: "Overview", keywords: "home dashboard" },
  { path: "/inbox", label: "Inbox", keywords: "conversations chats messages" },
  { path: "/analytics", label: "Analytics", keywords: "metrics stats reports" },
  { path: "/quality", label: "Quality", keywords: "conversation evidence monitoring csat handoff" },
  { path: "/aria", label: "AI Studio", keywords: "aria assistant ai studio persona tone voice guardrails tools escalation memory model prompt" },
  { path: "/assistant", label: "Assistant", keywords: "aria bot config persona" },
  { path: "/appearance", label: "Appearance Studio", keywords: "widget brand logo colors launcher avatar panel placement theme preset typography motion embed" },
  { path: "/engagement", label: "Proactive", keywords: "engagement rules teasers nudge" },
  { path: "/receptionist", label: "Receptionist", keywords: "phone voice setup business facts provider draft" },
  { path: "/calls", label: "Calls", keywords: "phone receptionist history recordings transcripts" },
  { path: "/playground", label: "Playground", keywords: "test simulate try" },
  { path: "/evaluation", label: "Evaluation", keywords: "scenario expected outcome test" },
  { path: "/knowledge-improvement", label: "Knowledge queue", keywords: "unresolved conversations draft review" },
  { path: "/settings", label: "Settings", keywords: "knowledge kb account password" },
  { path: "/install", label: "Install", keywords: "embed widget snippet setup" },
];

interface Scored {
  command: Command;
  score: number;
}

/**
 * Case-insensitive match + ranking. The title outranks the keywords at every
 * tier, which is the whole point of splitting them:
 *
 *   title startsWith (5) > title word-start (4) > title contains (3)
 *   > keyword word-start (2) > keyword contains (1) > no match (0)
 *
 * Every Navigate title begins "Go to …", so tier 5 effectively never fires for
 * them and tier 4 is what lets the page's own name win. Scoring title and
 * keywords together — as this did before — let any command whose *keywords*
 * mentioned "analytics" tie the Analytics page itself.
 */
function scoreCommand(command: Command, q: string): number {
  if (!q) return 1; // empty query: everything is eligible (Navigate + Action)
  const hay = `${command.title} ${command.keywords ?? ""}`.toLowerCase();
  const title = command.title.toLowerCase();
  const wordStart = new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`);
  if (title.startsWith(q)) return 5;
  if (wordStart.test(title)) return 4;
  if (title.includes(q)) return 3;
  if (wordStart.test(hay)) return 2;
  return hay.includes(q) ? 1 : 0;
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { session, signOut } = useAuth();
  const { services } = useEntitlements();
  const visiblePaths = useMemo(() => visibleNavPaths(services), [services]);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [convoCommands, setConvoCommands] = useState<Command[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const convoReqId = useRef(0);

  const go = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  // --- Static command registry (Navigate + Action). -----------------------
  const staticCommands = useMemo<Command[]>(() => {
    const nav: Command[] = NAV_ROUTES
      // null means "show everything" (both services active, or entitlements
      // not yet known) -- the same convention visibleNavPaths documents for
      // AppShell's own sidebar. F1 fix round 1 (I3): a route outside
      // GATED_NAV_PATHS (Overview, Inbox, Analytics, Settings, ...) is
      // SHARED and must survive regardless of `visiblePaths` -- the old
      // filter (`visiblePaths.has(r.path)`) dropped every shared route the
      // moment a gated account type narrowed `visiblePaths` to its own two
      // or three entries, which is the exact rule `filterNavGroups` in
      // navServices.mjs already applies to the sidebar.
      .filter((r) => !visiblePaths || !GATED_NAV_PATHS.has(r.path) || visiblePaths.has(r.path))
      .map((r) => ({
        id: `nav:${r.path}`,
        title: `Go to ${r.label}`,
        group: "Navigate",
        keywords: r.keywords,
        run: () => go(r.path),
      }));
    const actions: Command[] = [
      {
        id: "action:theme",
        title: "Toggle theme",
        group: "Action",
        hint: "Light / dark",
        keywords: "dark light mode appearance",
        run: () => {
          toggleTheme();
          onClose();
        },
      },
      {
        id: "action:new-rule",
        title: "New proactive rule",
        group: "Action",
        hint: "Proactive",
        keywords: "engagement teaser nudge create add",
        run: () => go("/engagement?new=1"),
      },
      {
        id: "action:publish-appearance",
        title: "Publish appearance",
        group: "Action",
        hint: "Appearance Studio",
        keywords: "widget theme publish save brand",
        run: () => go("/appearance"),
      },
      {
        id: "action:edit-persona",
        title: "Edit assistant persona",
        group: "Action",
        hint: "AI Studio",
        keywords: "tone voice persona guardrails prompt",
        run: () => go("/aria"),
      },
    ];
    if (session) {
      actions.push({
        id: "action:signout",
        title: "Sign out",
        group: "Action",
        keywords: "logout log out leave",
        run: () => {
          signOut();
          onClose();
        },
      });
    }
    return [...nav, ...actions];
  }, [go, onClose, session, signOut, visiblePaths]);

  // --- Reset transient state each time the palette opens; capture focus. ---
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setQuery("");
    setActive(0);
    setConvoCommands([]);
    // Focus the input on the next frame (after the enter transition starts).
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(t);
  }, [open]);

  // --- Restore focus to the previously-focused element on close. ----------
  useEffect(() => {
    if (open) return;
    const el = restoreFocusRef.current;
    if (el && typeof el.focus === "function") {
      // Defer so the element isn't mid-unmount.
      const t = window.setTimeout(() => el.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // --- Conversation results: debounced inboxApi.list() on non-empty query. -
  useEffect(() => {
    if (!open) return;
    const q = query.trim().toLowerCase();
    if (!q) {
      setConvoCommands([]);
      return;
    }
    const reqId = ++convoReqId.current;
    const t = window.setTimeout(() => {
      inboxApi
        .list()
        .then((r) => {
          if (reqId !== convoReqId.current) return; // a newer query superseded us
          const sessions = r.result?.sessions ?? [];
          const matches = sessions
            .filter((s) => {
              const name = String(s.customer?.name ?? "").toLowerCase();
              const email = String(s.customer?.email ?? "").toLowerCase();
              const preview = String(s.preview ?? "").toLowerCase();
              return (
                name.includes(q) ||
                email.includes(q) ||
                preview.includes(q) ||
                s.session_id.toLowerCase().includes(q)
              );
            })
            .slice(0, CONVO_CAP)
            .map<Command>((s) => {
              const label =
                (s.customer?.name && String(s.customer.name)) ||
                (s.customer?.email && String(s.customer.email)) ||
                "Visitor";
              return {
                id: `convo:${s.session_id}`,
                title: label,
                group: "Conversation",
                hint: s.preview ? s.preview.slice(0, 60) : undefined,
                run: () =>
                  go(`/inbox?session=${encodeURIComponent(s.session_id)}`),
              };
            });
          setConvoCommands(matches);
        })
        .catch(() => {
          // Inbox fetch failed — omit the Conversation group entirely; the
          // palette still works for Navigate + Action.
          if (reqId === convoReqId.current) setConvoCommands([]);
        });
    }, 140);
    return () => window.clearTimeout(t);
  }, [open, query, go]);

  // --- Filter + rank + group. ---------------------------------------------
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [...staticCommands, ...convoCommands];
    const scored: Scored[] = all
      .map((command) => ({ command, score: scoreCommand(command, q) }))
      // Conversation commands are pre-matched by the fetch effect; always keep.
      .filter((s) => s.command.group === "Conversation" || s.score > 0);

    const caps: Record<CommandGroup, number> = {
      Navigate: NAV_CAP,
      Action: ACTION_CAP,
      Conversation: CONVO_CAP,
    };

    return GROUP_ORDER.map((group) => {
      const items = scored
        .filter((s) => s.command.group === group)
        .sort((a, b) => b.score - a.score)
        .slice(0, caps[group])
        .map((s) => s.command);
      return { group, items };
    }).filter((g) => g.items.length > 0);
  }, [staticCommands, convoCommands, query]);

  // Flat list in render order — the index space the highlight moves through.
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Keep the active index in range as results change.
  useEffect(() => {
    setActive((i) => (flat.length === 0 ? 0 : Math.min(i, flat.length - 1)));
  }, [flat.length]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-cmd-index="${active}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  // --- Keyboard model (scoped to the palette card). -----------------------
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      flat[active]?.run();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "Tab") {
      // Focus trap — only the input is tabbable; keep focus inside.
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div
      className="cmdk-scrim modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="cmdk-card panel modal-card"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className="cmdk-search">
          <svg
            className="cmdk-search-ico"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            className="cmdk-input"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Search pages, actions, conversations…"
            aria-label="Search commands"
            aria-controls="cmdk-list"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <ul className="cmdk-list" id="cmdk-list" role="listbox" ref={listRef}>
          {flat.length === 0 ? (
            <li className="cmdk-empty" role="presentation">
              No matches for “{query.trim()}”.
            </li>
          ) : (
            groups.map((g) => (
              <li key={g.group} role="presentation">
                <div className="cmdk-group-label">{g.group}</div>
                <ul className="cmdk-group" role="presentation">
                  {g.items.map((cmd) => {
                    runningIndex += 1;
                    const index = runningIndex;
                    const selected = index === active;
                    return (
                      <li key={cmd.id} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          data-cmd-index={index}
                          className={`cmdk-row${selected ? " is-active" : ""}`}
                          onMouseMove={() => setActive(index)}
                          onClick={() => cmd.run()}
                        >
                          <span className="cmdk-row-title">{cmd.title}</span>
                          {cmd.hint ? (
                            <span className="cmdk-row-hint">{cmd.hint}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>

        <div className="cmdk-footer">
          <span className="cmdk-foot-item">
            <kbd className="cmdk-kbd">↑</kbd>
            <kbd className="cmdk-kbd">↓</kbd>
            to navigate
          </span>
          <span className="cmdk-foot-item">
            <kbd className="cmdk-kbd">⏎</kbd>
            to run
          </span>
          <span className="cmdk-foot-item">
            <kbd className="cmdk-kbd">esc</kbd>
            to close
          </span>
        </div>
      </div>
    </div>
  );
}
